import React, { useContext, useEffect } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { ColorModeContext } from '../index';
import logoLight from '../assets/images/logo1.png';
import logoDark from '../assets/images/mAuthenticate-logo.png';

const LIGHT_THEME = {
  '--bg': '#F4F7FB',
  '--card-bg': '#FFFFFF',
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--hairline': '#E2E8F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#64748B',
  '--shadow': '0 20px 45px rgba(11, 37, 69, 0.08)',
};

const DARK_THEME = {
  '--bg': '#0A1120',
  '--card-bg': '#111C33',
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--hairline': 'rgba(255, 255, 255, 0.1)',
  '--blue-tint': 'rgba(76, 154, 255, 0.14)',
  '--slate': '#94A3B8',
  '--shadow': '0 20px 45px rgba(0, 0, 0, 0.45)',
};

export default function NotFound() {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const isDark = mode === 'dark';
  const themeVars = isDark ? DARK_THEME : LIGHT_THEME;

  useEffect(() => {
    document.title = 'Page Not Found | mAuthenticate MFA';
  }, []);

  return (
    <Box
      style={themeVars}
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg)',
        backgroundImage: isDark
          ? 'radial-gradient(circle at 50% 18%, #14223E 0%, #0A1120 70%)'
          : 'radial-gradient(circle at 50% 18%, #EAF2FE 0%, #F4F7FB 70%)',
        p: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          backgroundColor: 'var(--card-bg)',
          borderRadius: '16px',
          border: '1px solid var(--hairline)',
          boxShadow: 'var(--shadow)',
          p: { xs: 3.5, sm: 5 },
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        {/* Brand Logo */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <img
            src={isDark ? logoDark : logoLight}
            alt="mAuthenticate"
            style={{
              height: 34,
              maxWidth: 200,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>

        {/* Security Shield Badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '18px',
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--blue-tint)',
            border: '1px solid var(--hairline)',
            color: 'var(--edge-blue)',
            boxShadow: isDark
              ? '0 6px 20px rgba(76, 154, 255, 0.2)'
              : '0 6px 20px rgba(31, 111, 235, 0.15)',
          }}
        >
          <SecurityOutlinedIcon sx={{ fontSize: 34 }} />
        </Box>

        {/* 404 Display */}
        <Typography
          sx={{
            fontFamily: 'Arial, sans-serif',
            fontSize: { xs: 68, sm: 84 },
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.04em',
            background: isDark
              ? 'linear-gradient(135deg, #4C9AFF 0%, #F4F7FB 100%)'
              : 'linear-gradient(135deg, #1F6FEB 0%, #0B2545 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          404
        </Typography>

        {/* Headline */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 700,
            color: 'var(--vault-navy)',
            fontSize: { xs: 20, sm: 22 },
            mb: 1.25,
          }}
        >
          Page Not Found
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Calibri, Arial, sans-serif',
            fontSize: 15,
            color: 'var(--slate)',
            maxWidth: 380,
            mx: 'auto',
            mb: 3.5,
            lineHeight: 1.55,
          }}
        >
          The page you are looking for doesn't exist, has been removed, or is temporarily unavailable.
        </Typography>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            component={RouterLink}
            to="/home"
            variant="contained"
            startIcon={<HomeOutlinedIcon />}
            fullWidth
            sx={{
              backgroundColor: 'var(--edge-blue)',
              color: '#FFFFFF',
              py: 1.1,
              px: 2.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'var(--edge-blue)',
                opacity: 0.92,
                boxShadow: '0 4px 14px rgba(31, 111, 235, 0.3)',
              },
            }}
          >
            Back to Dashboard
          </Button>

          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            startIcon={<ArrowBackOutlinedIcon />}
            fullWidth
            sx={{
              borderColor: 'var(--hairline)',
              color: 'var(--vault-navy)',
              py: 1.1,
              px: 2.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
              '&:hover': {
                borderColor: 'var(--edge-blue)',
                backgroundColor: 'var(--blue-tint)',
              },
            }}
          >
            Go Back
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
